import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';

/**
 * AI Quiz Taking Page
 * Dedicated UI for AI-generated quizzes
 */
const AIQuizPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { questions = [], config = {} } = location.state || {};
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(
    questions.length * 60 // 60 seconds per question
  );
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);

  // Redirect if no questions
  useEffect(() => {
    if (!questions.length) {
      toast.error('No quiz data found');
      navigate('/ai/quiz-generator');
      return;
    }
    window.scrollTo(0, 0);
  }, []);

  const currentQ = questions[currentQuestion];
  
  // Convert options to array - handles multiple formats
  const options = useMemo(() => {
    if (!currentQ) {
      return [];
    }
    
    // Format 1: options object {A: 'text', B: 'text'}
    if (currentQ.options && typeof currentQ.options === 'object' && !Array.isArray(currentQ.options)) {
      return Object.entries(currentQ.options).map(([key, value]) => ({
        label: key,
        text: value
      }));
    }
    
    // Format 2: options is already an array
    if (Array.isArray(currentQ.options)) {
      return currentQ.options.map((opt, idx) => ({
        label: opt.label || String.fromCharCode(65 + idx),
        text: opt.text || opt
      }));
    }
    
    // Format 3: opt_a, opt_b, opt_c, opt_d properties (MCQ)
    const optionsArray = [];
    if (currentQ.opt_a) optionsArray.push({ label: 'A', text: currentQ.opt_a });
    if (currentQ.opt_b) optionsArray.push({ label: 'B', text: currentQ.opt_b });
    if (currentQ.opt_c) optionsArray.push({ label: 'C', text: currentQ.opt_c });
    if (currentQ.opt_d) optionsArray.push({ label: 'D', text: currentQ.opt_d });
    
    if (optionsArray.length > 0) {
      return optionsArray;
    }
    
    // Format 4: True/False question
    if (currentQ.correct_answer && 
        (currentQ.correct_answer.toLowerCase() === 'true' || 
         currentQ.correct_answer.toLowerCase() === 'false')) {
      return [
        { label: 'True', text: 'True' },
        { label: 'False', text: 'False' }
      ];
    }
    
    return [];
  }, [currentQuestion, questions]);

  const handleNext = useCallback(() => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      window.scrollTo(0, 0);
    }
  }, [currentQuestion, questions.length]);

  const handlePrevious = useCallback(() => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      window.scrollTo(0, 0);
    }
  }, [currentQuestion]);

  const handleSubmit = useCallback(() => {
    // Calculate results
    let correct = 0;
    let incorrect = 0;
    let unanswered = 0;

    questions.forEach((q, idx) => {
      const userAnswer = answers[idx];
      if (!userAnswer) {
        unanswered++;
      } else {
        // Handle different correct_answer formats
        let correctAnswer = q.correct_answer;
        
        // If correct_answer is like "C", but option label has full text "C) text"
        // Extract just the letter
        if (correctAnswer && correctAnswer.length === 1) {
          // Already just a letter like "A", "B", "C", "D"
        } else if (correctAnswer) {
          // Extract first character if it's like "C) text"
          correctAnswer = correctAnswer.charAt(0);
        }
        
        if (userAnswer === correctAnswer || userAnswer === q.correct_answer) {
          correct++;
        } else {
          incorrect++;
        }
      }
    });

    const totalMarks = questions.length;
    const marksObtained = correct;
    const percentage = ((marksObtained / totalMarks) * 100).toFixed(2);

    setResults({
      correct,
      incorrect,
      unanswered,
      totalQuestions: questions.length,
      marksObtained,
      totalMarks,
      percentage,
      timeTaken: (questions.length * 60) - timeLeft
    });

    setShowResults(true);
  }, [questions, answers, timeLeft]);

  // Timer - placed after handleSubmit is defined
  useEffect(() => {
    if (showResults) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showResults, handleSubmit]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Results View
  if (showResults && results) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
        <PublicHeader />
        
        <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 max-w-4xl">
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">
                {results.percentage >= 80 ? '🎉' : results.percentage >= 60 ? '👍' : '📚'}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                Quiz Completed!
              </h1>
              <p className="text-gray-600">
                {config.examDetails?.title || 'AI Generated Quiz'}
              </p>
            </div>

            {/* Score Card */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <div className="bg-green-50 rounded-lg p-4 text-center border-2 border-green-200">
                <div className="text-3xl font-bold text-green-600">{results.correct}</div>
                <div className="text-sm text-green-800 mt-1">Correct</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center border-2 border-red-200">
                <div className="text-3xl font-bold text-red-600">{results.incorrect}</div>
                <div className="text-sm text-red-800 mt-1">Incorrect</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center border-2 border-gray-200">
                <div className="text-3xl font-bold text-gray-600">{results.unanswered}</div>
                <div className="text-sm text-gray-800 mt-1">Unanswered</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center border-2 border-blue-200">
                <div className="text-3xl font-bold text-blue-600">{results.percentage}%</div>
                <div className="text-sm text-blue-800 mt-1">Score</div>
              </div>
            </div>

            {/* Performance Message */}
            <div className={`p-4 rounded-lg mb-6 ${
              results.percentage >= 80 ? 'bg-green-100 text-green-800 border border-green-300' :
              results.percentage >= 60 ? 'bg-blue-100 text-blue-800 border border-blue-300' :
              'bg-orange-100 text-orange-800 border border-orange-300'
            }`}>
              <p className="font-semibold text-center">
                {results.percentage >= 80 ? '🌟 Excellent! You have great understanding of this topic!' :
                 results.percentage >= 60 ? '👏 Good job! Keep practicing to improve further.' :
                 '💪 Keep learning! Review the explanations and try again.'}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-8 text-sm">
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-gray-600">Time Taken:</span>
                <span className="font-semibold text-gray-800 ml-2">
                  {formatTime(results.timeTaken)}
                </span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <span className="text-gray-600">Marks:</span>
                <span className="font-semibold text-gray-800 ml-2">
                  {results.marksObtained}/{results.totalMarks}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowResults(false);
                  setCurrentQuestion(0);
                  setAnswers({});
                  setTimeLeft(questions.length * 60);
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Review Answers
              </button>
              <button
                onClick={() => navigate('/ai/quiz-generator')}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Generate New Quiz
              </button>
              <button
                onClick={() => navigate('/ai/features')}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Back to AI Features
              </button>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Quiz Taking View
  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <PublicHeader />
      
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8">
        {/* Header Bar */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-800">
                {config.examDetails?.title || 'AI Generated Quiz'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Question {currentQuestion + 1} of {questions.length}
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Progress */}
              <div className="text-sm">
                <span className="text-gray-600">Answered:</span>
                <span className="font-semibold text-blue-600 ml-2">
                  {Object.keys(answers).length}/{questions.length}
                </span>
              </div>
              
              {/* Timer */}
              <div className={`px-4 py-2 rounded-lg font-bold ${
                timeLeft < 60 ? 'bg-red-100 text-red-700' :
                timeLeft < 300 ? 'bg-orange-100 text-orange-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                ⏱️ {formatTime(timeLeft)}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Area */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-md p-6">
              {/* Question */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex-shrink-0 bg-purple-600 text-white font-bold px-3 py-1 rounded">
                    Q{currentQuestion + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-lg text-gray-800 leading-relaxed">
                      {currentQ?.question}
                    </p>
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="space-y-3 mb-6">
                {options.length === 0 ? (
                  <div className="text-red-600 p-4 bg-red-50 rounded-lg border-2 border-red-300">
                    <p className="font-bold mb-2">⚠️ No options available for this question</p>
                    <p className="text-sm">Question: {currentQ?.question}</p>
                  </div>
                ) : (
                  options.map((option, idx) => {
                    const optionLabel = option.label;
                    const userAnswer = answers[currentQuestion];
                    const isSelected = userAnswer === optionLabel;
                    
                    // Clean option text - remove "A) ", "B: ", etc. prefixes
                    let optionText = String(option.text || option.value || option);
                    optionText = optionText.replace(/^[A-D][\)\:\.]?\s*/, '').trim();
                    
                    return (
                      <button
                        key={`option-${idx}-${optionLabel}`}
                        type="button"
                        onClick={() => {
                          setAnswers(prev => ({
                            ...prev,
                            [currentQuestion]: optionLabel
                          }));
                        }}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            isSelected
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-700'
                          }`}>
                            {optionLabel}
                          </span>
                          <span className="flex-1 text-gray-800 text-sm sm:text-base">
                            {optionText}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 font-semibold rounded-lg transition-colors"
                >
                  ← Previous
                </button>
                
                {currentQuestion < questions.length - 1 ? (
                  <button
                    onClick={handleNext}
                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    Next →
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    Submit Quiz
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Question Palette */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-4 sticky top-4">
              <h3 className="font-bold text-gray-800 mb-4">Questions</h3>
              
              <div className="grid grid-cols-5 lg:grid-cols-4 gap-2">
                {questions.map((_, idx) => {
                  const isAnswered = answers[idx] !== undefined;
                  const isCurrent = idx === currentQuestion;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        setCurrentQuestion(idx);
                        window.scrollTo(0, 0);
                      }}
                      className={`aspect-square rounded-lg font-semibold text-sm transition-colors ${
                        isCurrent
                          ? 'bg-purple-600 text-white'
                          : isAnswered
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-6 space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-green-500"></div>
                  <span className="text-gray-600">Answered</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-gray-200"></div>
                  <span className="text-gray-600">Not Answered</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-purple-600"></div>
                  <span className="text-gray-600">Current</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AIQuizPage;
